/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import {
  Loader2, Leaf, ArrowUp, ArrowDown, Minus, Building2,
  HandHeart, Sparkles, Compass, ShieldCheck, Wind,
} from 'lucide-react';

/**
 * Weekly War Room™ — Phase 4c.
 *
 * ONE consolidated ecosystem-stewardship dashboard. Holistic, calm,
 * situationally aware. Never tabs, never reporting. Surfaces where the
 * territory is asking for support. Reflection Notes™ are NEVER surfaced.
 *
 * Sections (single page, vertical, generous spacing):
 *   1. Ecosystem Pulse
 *   2. Territory Calibration
 *   3. Human Stewardship
 *   4. Movement Integrity
 *   5. Suggested Stewardship Actions
 */
export default function WeeklyWarRoomPage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/terra-growth/war-room?days=${days}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="weekly-war-room-page" className="terra-page-pad max-w-6xl mx-auto px-8 py-12">
        <Header days={days} setDays={setDays} />
        {loading ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-16 text-center text-[#8A8681]">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Composing this week's stewardship view…
          </div>
        ) : !data ? (
          <div data-testid="war-room-error"
               className="rounded-2xl border border-[#EBE8E3] bg-white p-16 text-center text-[#8A8681]">
            Could not compose the War Room right now.
          </div>
        ) : (
          <div className="space-y-12">
            <EcosystemPulse pulse={data.ecosystem_pulse} />
            <TerritoryCalibration calibration={data.territory_calibration} />
            <HumanStewardship stew={data.human_stewardship} />
            <MovementIntegrity integ={data.movement_integrity} />
            <StewardshipActions actions={data.suggested_stewardship_actions} />
            <p data-testid="war-room-philosophy"
               className="text-[11px] text-[#8A8681] italic max-w-2xl pt-6 border-t border-[#F4F2EE]">
              {data.philosophy_note}
            </p>
          </div>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Header
// ═══════════════════════════════════════════════════════════════════

function Header({ days, setDays }) {
  return (
    <header className="mb-10 flex flex-wrap items-end justify-between gap-4">
      <div className="space-y-3 max-w-2xl">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#8C6239] flex items-center gap-2">
          <Compass className="w-3 h-3" /> Terra Growth OS™ · Stewardship
        </p>
        <h1 className="text-4xl font-['Outfit'] font-light text-[#2C2A29] tracking-tight">
          Weekly War Room™
        </h1>
        <p className="text-sm text-[#8A8681] font-['Work_Sans'] leading-relaxed">
          Where movement is slowing, where depth is softening, where support is
          needed, where restoration is needed. A calm, holistic look at the
          ecosystem — never management, never reporting. Stewardship.
        </p>
      </div>
      <div className="flex items-center gap-2">
        {[7, 14, 30].map(d => (
          <button key={d} data-testid={`war-room-days-${d}`} onClick={() => setDays(d)}
                  className={`text-[11px] rounded-full px-3.5 py-1.5 border transition ${
                    days === d
                      ? 'bg-[#4A5D4E] text-white border-[#4A5D4E]'
                      : 'bg-white text-[#5C5955] border-[#D8D4CE] hover:border-[#B5B0A8]'}`}>
            last {d}d
          </button>
        ))}
      </div>
    </header>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Section 1 — Ecosystem Pulse
// ═══════════════════════════════════════════════════════════════════

function EcosystemPulse({ pulse }) {
  if (!pulse) return null;
  const posture = pulse.territory_posture || {};
  const mom = pulse.stabilization_momentum || {};

  return (
    <section data-testid="war-room-ecosystem-pulse" className="space-y-5">
      <SectionHeader index="01" icon={Wind} title="Ecosystem Pulse" sub="The territory's quiet weekly breath." />

      <div className="rounded-2xl border border-[#E5D6BE] bg-[#FAF1E1]/40 p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-4 mb-1">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#8C6239]">Territory posture</p>
          <PostureChip posture={posture} testId="war-room-territory-posture" />
        </div>
        <p className="text-sm text-[#5C3D1F] leading-relaxed">{posture.description}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat testId="pulse-movements"
              label="Forward movements"
              value={pulse.movements?.forwards ?? 0}
              sub={`↓ ${pulse.movements?.regressions ?? 0} regressions · Δ ${formatDelta(pulse.movements?.delta_vs_prior)}`} />
        <Stat testId="pulse-stuck"
              label="Holding rungs"
              value={pulse.stuck_total ?? 0}
              sub="leads in need of movement" />
        <Stat testId="pulse-right-subscriber"
              label="Right-subscriber emergence"
              value={pulse.right_subscriber_emerged ?? 0}
              sub="entered subscriber state" />
        <Stat testId="pulse-depth"
              label="Conversation depth"
              value={`${pulse.depth_stabilization?.current_avg_pct ?? 0}%`}
              sub={`Δ ${formatDelta(pulse.depth_stabilization?.delta)} vs prior window`} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <article data-testid="pulse-stabilization-momentum"
                 className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-3">
            Stabilization Momentum
          </p>
          <div className="space-y-2">
            <MomentumLine label="Movement" delta={mom.movement_delta} />
            <MomentumLine label="Depth stabilization" delta={mom.depth_delta} suffix="%" />
            <p className="text-[11px] text-[#5C5955] italic mt-3 leading-relaxed">
              {momentumNarrative(mom.divergence)}
            </p>
          </div>
        </article>

        <article data-testid="pulse-strongest-maturation"
                 className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-2">
            Strongest maturation
          </p>
          {pulse.strongest_maturation ? (
            <>
              <p className="text-base text-[#2C2A29] truncate">{pulse.strongest_maturation.centre_id}</p>
              <p className="text-[11px] text-[#5C5955] mt-0.5">
                {pulse.strongest_maturation.maturity_label} · advocate density{' '}
                <span className="tabular-nums">{pulse.strongest_maturation.advocate_density}%</span>
              </p>
            </>
          ) : (
            <p className="text-[11px] text-[#B5B0A8]">No centre yet shows clear maturation.</p>
          )}
        </article>
      </div>

      <SlowingZones zones={pulse.movement_slowing_zones} />
    </section>
  );
}

function PostureChip({ posture, testId }) {
  const tone = TONE_MAP[posture.tone] || TONE_MAP.stone;
  return (
    <span data-testid={testId}
          className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border ${tone}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
      {posture.label || posture.code || '—'}
    </span>
  );
}

function MomentumLine({ label, delta, suffix = '' }) {
  const v = Number(delta) || 0;
  const Icon = v > 0 ? ArrowUp : v < 0 ? ArrowDown : Minus;
  const color = v > 0 ? 'text-emerald-700' : v < 0 ? 'text-stone-500' : 'text-[#8A8681]';
  return (
    <div className="flex items-center justify-between text-[12px]">
      <span className="text-[#5C5955]">{label}</span>
      <span className={`tabular-nums inline-flex items-center gap-1 ${color}`}>
        <Icon className="w-3 h-3" />
        {v > 0 ? '+' : ''}{v}{suffix}
      </span>
    </div>
  );
}

function momentumNarrative(div) {
  switch (div) {
    case 'movement_increasing_depth_softening':
      return 'Movement is increasing, but depth is softening. Slow down — let conversations land.';
    case 'movement_softening_depth_softening':
      return 'Both movement and depth are softening. A gentle ecosystem touchpoint may help.';
    case 'movement_and_depth_aligned':
      return 'Movement and depth are stabilizing together. Hold the rhythm.';
    case 'calm_steady':
      return 'Calm, steady week. Hold space.';
    default:
      return 'Mixed signals. Look across centres before acting.';
  }
}

function SlowingZones({ zones }) {
  if (!zones?.length) return null;
  return (
    <article data-testid="pulse-slowing-zones"
             className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-3">
        Where movement is slowing
      </p>
      <ul className="space-y-2">
        {zones.map(z => (
          <li key={z.ecosystem_code}
              data-testid={`slowing-zone-${z.ecosystem_code}`}
              className="flex items-center justify-between gap-3 text-[12px]">
            <span className="text-[#2C2A29] truncate">{z.ecosystem_code}</span>
            <span className="tabular-nums text-[#8A8681]">
              {z.velocity_pct}% velocity · {z.sample_count} transitions
            </span>
          </li>
        ))}
      </ul>
    </article>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Section 2 — Territory Calibration
// ═══════════════════════════════════════════════════════════════════

function TerritoryCalibration({ calibration }) {
  const centres = calibration?.centres || [];
  return (
    <section data-testid="war-room-territory-calibration" className="space-y-5">
      <SectionHeader index="02" icon={Building2} title="Territory Calibration"
                     sub="How each centre is breathing this week." />
      {centres.length === 0 ? (
        <p data-testid="territory-empty"
           className="text-[11px] text-[#B5B0A8] italic">
          No centre-level signal in scope yet.
        </p>
      ) : (
        <ul className="space-y-3">
          {centres.map(c => <CentreRow key={c.centre_id} c={c} />)}
        </ul>
      )}
    </section>
  );
}

function CentreRow({ c }) {
  return (
    <li data-testid={`centre-${c.centre_id}`}
        className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
      <header className="flex items-baseline justify-between gap-3 mb-3">
        <p className="text-base text-[#2C2A29] truncate">{c.centre_id}</p>
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#8C6239]">
          {c.maturity_label}
        </span>
      </header>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniStat label="Lead total"   value={c.lead_total} />
        <MiniStat label="Movements"    value={c.movements_in_window} />
        <MiniStat label="Holding"      value={c.stuck_count} />
        <MiniStat label="Advocate density" value={`${c.advocate_density}%`} />
      </div>
      {c.support_zones?.length > 0 && (
        <p className="text-[11px] text-[#5C5955] mt-3">
          Support zones:{' '}
          <span className="text-[#8C6239]">
            {c.support_zones.join(' · ')}
          </span>
        </p>
      )}
    </li>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Section 3 — Human Stewardship (NO ranking, NO reflection notes)
// ═══════════════════════════════════════════════════════════════════

function HumanStewardship({ stew }) {
  if (!stew) return null;
  return (
    <section data-testid="war-room-human-stewardship" className="space-y-5">
      <SectionHeader index="03" icon={HandHeart} title="Human Stewardship"
                     sub="The people who hold the ecosystem." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <CountCard testId="stew-restoration"
                   label="Counselors needing restoration"
                   value={stew.needing_restoration?.length || 0}
                   tone="amber" />
        <CountCard testId="stew-reflective-softening"
                   label="Reflective space softening"
                   value={stew.reflective_space_softening?.length || 0}
                   tone="stone" />
        <CountCard testId="stew-rhythm-softening"
                   label="Recovery rhythm softening"
                   value={stew.recovery_rhythm_softening?.length || 0}
                   tone="stone" />
      </div>

      {stew.needing_restoration?.length > 0 && (
        <article data-testid="stew-restoration-list"
                 className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-3">
            Holding strain — support priority
          </p>
          <ul className="space-y-1.5">
            {stew.needing_restoration.map(m => (
              <li key={m.user_id}
                  data-testid={`stew-restoration-${m.user_id}`}
                  className="flex items-center justify-between gap-3 text-[12px]">
                <span className="text-[#2C2A29] truncate">{m.name}</span>
                <PostureChip posture={m.posture} testId={`stew-posture-${m.user_id}`} />
              </li>
            ))}
          </ul>
        </article>
      )}

      <article data-testid="stew-mentorship"
               className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-3 flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Mentorship support
        </p>
        <div className="grid grid-cols-2 gap-3">
          <MiniStat label="Pairings ready"        value={stew.mentorship?.pairing_count ?? 0} />
          <MiniStat label="Co-holding suggested"  value={stew.mentorship?.co_holding_count ?? 0} />
        </div>
      </article>

      <p className="text-[11px] text-[#8A8681] italic max-w-2xl">
        {stew.framing_note}
      </p>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Section 4 — Movement Integrity
// ═══════════════════════════════════════════════════════════════════

function MovementIntegrity({ integ }) {
  if (!integ) return null;
  return (
    <section data-testid="war-room-movement-integrity" className="space-y-5">
      <SectionHeader index="04" icon={ShieldCheck} title="Movement Integrity"
                     sub="When perception shifts, is depth keeping pace?" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <article data-testid="integ-depth-shift"
                 className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-3">
            Conversation depth shift
          </p>
          <div className="flex items-baseline justify-between">
            <span className="text-3xl font-['Outfit'] tabular-nums text-[#2C2A29]">
              {integ.depth_quality_shift?.current_avg_pct ?? 0}%
            </span>
            <span className={`text-[11px] tabular-nums ${
              (integ.depth_quality_shift?.delta || 0) >= 0 ? 'text-emerald-700' : 'text-stone-500'}`}>
              Δ {formatDelta(integ.depth_quality_shift?.delta)}%
            </span>
          </div>
          <p className="text-[10px] text-[#8A8681] mt-1">
            current vs prior window
          </p>
        </article>
        <article data-testid="integ-false-positive"
                 className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-3">
            False-positive movement risk
          </p>
          <p className="text-3xl font-['Outfit'] tabular-nums text-[#2C2A29]">
            {integ.false_positive_movement_risk ?? 0}
          </p>
          <p className="text-[10px] text-[#8A8681] mt-1">
            leads moved forward but depth is softening
          </p>
        </article>
        <article data-testid="integ-leakage"
                 className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-3">
            Leakage stabilization
          </p>
          <div className="flex items-baseline justify-between">
            <span className="text-3xl font-['Outfit'] tabular-nums text-[#2C2A29]">
              {integ.leakage_stabilization?.current ?? 0}
            </span>
            <span className={`text-[11px] tabular-nums ${
              (integ.leakage_stabilization?.delta || 0) <= 0 ? 'text-emerald-700' : 'text-stone-500'}`}>
              Δ {formatDelta(integ.leakage_stabilization?.delta)}
            </span>
          </div>
          <p className="text-[10px] text-[#8A8681] mt-1">
            holding leads vs prior cycle
          </p>
        </article>
        <article data-testid="integ-family-alignment"
                 className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-3">
            Family alignment softening
          </p>
          <p className="text-3xl font-['Outfit'] tabular-nums text-[#2C2A29]">
            {integ.family_alignment_softening ?? 0}
          </p>
          <p className="text-[10px] text-[#8A8681] mt-1">
            evaluation-band leads with no family-alignment touchpoint yet
          </p>
        </article>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Section 5 — Suggested Stewardship Actions
// ═══════════════════════════════════════════════════════════════════

function StewardshipActions({ actions }) {
  if (!actions?.length) return null;
  return (
    <section data-testid="war-room-actions" className="space-y-5">
      <SectionHeader index="05" icon={Leaf} title="Suggested Stewardship Actions"
                     sub="Already-wired, deterministic, never autonomous." />
      <ul className="space-y-3">
        {actions.map((a, i) => (
          <li key={`${a.code}-${i}`}
              data-testid={`action-${a.code}`}
              className="rounded-2xl border border-[#E5D6BE] bg-[#FAF1E1]/40 p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#8C6239]">
                {a.label}
              </p>
              {a.wired_path && (
                <a data-testid={`action-link-${a.code}`}
                   href={a.wired_path}
                   className="text-[10px] uppercase tracking-[0.18em] text-[#5C3D1F] underline-offset-4 hover:underline">
                  open →
                </a>
              )}
            </div>
            <p className="text-sm text-[#5C3D1F] mt-2 leading-relaxed">{a.detail}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Shared
// ═══════════════════════════════════════════════════════════════════

const TONE_MAP = {
  emerald: 'border-emerald-200 bg-emerald-50/50 text-emerald-800',
  sky:     'border-sky-200 bg-sky-50/50 text-sky-800',
  amber:   'border-amber-200 bg-amber-50/50 text-amber-800',
  stone:   'border-stone-200 bg-stone-50/50 text-stone-700',
  violet:  'border-violet-200 bg-violet-50/50 text-violet-800',
};

function SectionHeader({ index, icon: Icon, title, sub }) {
  return (
    <header className="space-y-1">
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#8C6239] flex items-center gap-2">
        <span className="tabular-nums">{index}</span>
        <Icon className="w-3 h-3" /> {title}
      </p>
      <p className="text-[12px] text-[#8A8681] italic">{sub}</p>
    </header>
  );
}

function Stat({ testId, label, value, sub }) {
  return (
    <article data-testid={testId} className="rounded-2xl border border-[#EBE8E3] bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681]">{label}</p>
      <p className="text-3xl font-['Outfit'] tabular-nums text-[#2C2A29] mt-1">{value}</p>
      <p className="text-[10px] text-[#8A8681] mt-0.5">{sub}</p>
    </article>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681]">{label}</p>
      <p className="text-xl font-['Outfit'] tabular-nums text-[#2C2A29] mt-0.5">{value}</p>
    </div>
  );
}

function CountCard({ testId, label, value, tone = 'stone' }) {
  return (
    <article data-testid={testId}
             className={`rounded-2xl border ${TONE_MAP[tone] || TONE_MAP.stone} p-5`}>
      <p className="text-[10px] uppercase tracking-[0.16em] opacity-70">{label}</p>
      <p className="text-3xl font-['Outfit'] tabular-nums mt-1">{value}</p>
    </article>
  );
}

function formatDelta(d) {
  if (d == null) return '—';
  const v = Number(d);
  if (v > 0) return `+${v}`;
  return `${v}`;
}
